import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api.js";

/**
 * Mutationen für Servicebeauftragungen. Alle invalidieren den
 * Customer-360-Query — die Beauftragungs-Karte und die Projekt-Tabelle
 * ziehen ihre Daten von dort.
 */

export interface EngagementRef {
  id: string;
  segmentId: string;
  name: string;
  leadEmployeeId: string | null;
  leadEmployeeName: string | null;
  deputyEmployeeId: string | null;
  deputyEmployeeName: string | null;
  activeProjectCount: number;
  implementers: Array<{ employeeId: string; fullName: string }>;
  targetYear: number;
  revenueTargetEur: string | null;
  growthTargetPct: string | null;
  /** Summe des Vorjahresumsatzes der Beauftragung — Basis der Zielpflege. */
  previousYearRevenueEur: string | null;
  /**
   * Jira-Kundenabteilung, die diese Beauftragung abbildet (F1). Ist sie
   * gesetzt, führen die Assets Name und Service Lead — beide sind dann nicht
   * bearbeitbar (F4).
   */
  jiraAssetId: string | null;
  jiraAssetKey: string | null;
  jiraAssetLabel: string | null;
  /**
   * Zeitpunkt der letzten Übernahme aus dem Asset. **null = noch nie** — dann
   * ist die Beauftragung zwar verknüpft, Name und Lead stammen aber weiterhin
   * aus dem CRM. Die Oberfläche muss das sagen, statt Übernahme zu behaupten.
   */
  jiraAssetSyncedAt: string | null;
}

function useInvalidateCustomer(customerId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["customers", customerId] });
    void qc.invalidateQueries({ queryKey: ["customers"] });
    // Lead-Cockpit speist sich aus denselben Daten (Ziele, Stellv., Umsetzer).
    void qc.invalidateQueries({ queryKey: ["kpi"] });
  };
}

export function useCreateEngagement(customerId: string) {
  const invalidate = useInvalidateCustomer(customerId);
  return useMutation({
    mutationFn: (payload: {
      segmentId: string;
      name: string;
      leadEmployeeId?: string | null;
      deputyEmployeeId?: string | null;
    }) =>
      api.post<{ id: string }>(`/customers/${customerId}/engagements`, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateEngagement(customerId: string) {
  const invalidate = useInvalidateCustomer(customerId);
  return useMutation({
    mutationFn: ({
      engagementId,
      ...payload
    }: {
      engagementId: string;
      name?: string;
      leadEmployeeId?: string | null;
      deputyEmployeeId?: string | null;
      implementerIds?: string[];
    }) => api.patch<void>(`/engagements/${engagementId}`, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteEngagement(customerId: string) {
  const invalidate = useInvalidateCustomer(customerId);
  return useMutation({
    mutationFn: ({ engagementId }: { engagementId: string }) =>
      api.delete<void>(`/engagements/${engagementId}`),
    onSuccess: invalidate,
  });
}

export function useAssignProjectEngagement(customerId: string) {
  const invalidate = useInvalidateCustomer(customerId);
  return useMutation({
    mutationFn: ({
      projectId,
      engagementId,
    }: {
      projectId: string;
      engagementId: string | null;
    }) =>
      api.put<void>(`/service-projects/${projectId}/engagement`, {
        engagementId,
      }),
    onSuccess: invalidate,
  });
}

/** Sammel-Zuordnung mehrerer Projekte einer Zelle (null = aushängen). */
export function useBulkAssignProjectEngagement(customerId: string) {
  const invalidate = useInvalidateCustomer(customerId);
  return useMutation({
    mutationFn: ({
      projectIds,
      engagementId,
    }: {
      projectIds: string[];
      engagementId: string | null;
    }) =>
      api.post<{ updated: number }>(`/service-projects/engagement-bulk`, {
        projectIds,
        engagementId,
      }),
    onSuccess: invalidate,
  });
}

export function useSetEngagementTarget(customerId: string) {
  const invalidate = useInvalidateCustomer(customerId);
  return useMutation({
    mutationFn: ({
      engagementId,
      year,
      revenueTargetEur,
      growthTargetPct,
      begruendung,
    }: {
      engagementId: string;
      year: number;
      revenueTargetEur: number | null;
      growthTargetPct: number | null;
      /**
       * Pflicht, sobald die Zielrunde eingefroren ist — der Server erzwingt
       * es (400 `BEGRUENDUNG_FEHLT`), nicht dieser Hook. Bei offener Runde
       * ungenutzt, deshalb optional.
       */
      begruendung?: string;
    }) =>
      api.put<void>(`/engagements/${engagementId}/targets/${year}`, {
        revenueTargetEur,
        growthTargetPct,
        begruendung,
      }),
    onSuccess: invalidate,
  });
}
